// Core domain model (gym-tracker-spec.md section 7). Pure types only: no Dexie,
// no React, no DOM. Shared by the app, the sync layer, the Worker DTOs, and the
// setup scripts. All weights are stored normalized to TOTAL kg internally; the
// per-side / per-hand ambiguity is resolved at the display boundary only.

// Spec section 7 enumerates the core groups; front_delt is a faithful extension so
// overhead pressing is not mislabeled as side_delt (which would corrupt the
// section-6 side-delt frequency check).
export type MuscleGroup =
  | 'chest'
  | 'back'
  | 'side_delt'
  | 'front_delt'
  | 'rear_delt'
  | 'biceps'
  | 'triceps'
  | 'quad'
  | 'hamstring'
  | 'glute'
  | 'core'
  | 'calf';

export const MUSCLE_GROUPS: MuscleGroup[] = [
  'chest', 'back', 'side_delt', 'front_delt', 'rear_delt', 'biceps', 'triceps',
  'quad', 'hamstring', 'glute', 'core', 'calf',
];

export const MUSCLE_LABEL: Record<MuscleGroup, string> = {
  chest: 'Chest',
  back: 'Back',
  side_delt: 'Side delt',
  front_delt: 'Front delt',
  rear_delt: 'Rear delt',
  biceps: 'Biceps',
  triceps: 'Triceps',
  quad: 'Quads',
  hamstring: 'Hamstrings',
  glute: 'Glutes',
  core: 'Core',
  calf: 'Calves',
};

// straight_sets: fixed weight, double progression, last set to failure.
// amrap: every set to concentric failure (weighted bodyweight, e.g. dips).
// ramp_top_backoff: ramp, one heavy top set (6-8, never to failure), back-offs at -10%.
export type SetStructure = 'straight_sets' | 'amrap' | 'ramp_top_backoff';

// total: the logged number is the full load. per_side: the logged number is the
// per-hand load (Marko's confirmed dumbbell convention); internal total = 2x.
export type WeightEntry = 'total' | 'per_side';

export type EquipmentArea = 'upstairs' | 'downstairs' | 'either';

// Which physical gym a session happened in. The same movement on a different
// gym's machine is a different loading scale AND a different feel, so weights are
// only comparable within one gym: targets, the progression engine, and stall
// detection are all scoped by gymId (see repo.exerciseHistory). Volume and
// frequency deliberately stay global — a hard set is a hard set anywhere.
// Adding a gym = extend this union + add an entry to GYMS in domain/gym.ts.
export type GymId = 'fitx' | 'fitness-first';

export interface Gym {
  id: GymId;
  name: string;
  // Shown in pickers/badges where the full name is too long.
  shortName: string;
}

// First-class injury-signal fields. The medial-epicondyle tendinosis that
// motivated them resolved in 2026-09; the fields stay as an early-warning monitor,
// since a quiet elbow is what justifies each step up in pulling load.
export type Feel = 'none' | 'aware' | 'pain';
export const FEELS: Feel[] = ['none', 'aware', 'pain'];

export type GripVariant = 'supinated' | 'hammer' | 'neutral' | 'wide' | 'close' | 'rope';
export const GRIP_VARIANTS: GripVariant[] = ['supinated', 'hammer', 'neutral', 'wide', 'close', 'rope'];

export interface RepRange {
  bottom: number;
  top: number;
}

export interface Exercise {
  id: string;
  name: string;
  muscleGroup: MuscleGroup;
  structure: SetStructure;
  defaultRepRange: RepRange;
  // Smallest weight step, stored in TOTAL kg. For a per_side dumbbell that jumps
  // 2.5 kg per hand, increment is 5 (the display layer shows 2.5). This is the
  // default; a gym whose rack/stack steps differently overrides it below.
  increment: number;
  // Sparse per-gym override of `increment`, TOTAL kg. Only set where a gym really
  // differs (e.g. Fitness First's dumbbells go up in 2 kg/hand, not 2.5), so the
  // +/- buttons land on weights that physically exist there. Resolved once at the
  // boundary by resolveExerciseForGym; nothing downstream reads this directly.
  incrementByGym?: Partial<Record<GymId, number>>;
  equipmentArea: EquipmentArea;
  weightEntry: WeightEntry;
  // Bodyweight-based (dips, Nordic curls): weightKg on a set is the ADDED external
  // load (can be 0 or negative for assisted); absolute load = bodyweight + added.
  bodyweight: boolean;
  tracksElbowFeel: boolean;
  tracksForearmFeel: boolean;
  tracksGrip: boolean;
  // Never taken to failure (balance/spine-loaded, e.g. Bulgarian split squats).
  neverToFailure: boolean;
  // Timed hold (e.g. plank): there is no external load and the set's `reps` field
  // stores SECONDS held, not repetitions. The set row shows a countdown timer
  // instead of weight/rep steppers; progression is "beat last time's seconds".
  timed?: boolean;
  notes: string;
  archived: boolean;
}

export interface SetLog {
  setNumber: number;
  // Ramp/warm-up set: excluded from progression and volume math (spec section 7).
  isWarmup: boolean;
  // TOTAL kg. For bodyweight exercises this is the ADDED load only.
  weightKg: number;
  reps: number;
  // 2 | 1 | 0 (0 = failure). null on warm-up sets (no RIR target).
  rir: number | null;
  isFailure: boolean;
  // Draft-only: false for a pre-filled placeholder not yet performed. Unlogged
  // sets are dropped when the session is finished, so finished/seeded sessions
  // only ever contain performed sets (undefined is treated as logged).
  logged?: boolean;
}

export interface ExerciseEntry {
  exerciseId: string;
  order: number;
  // If swapped mid-session for busy equipment, the original exercise id.
  substitutedFrom: string | null;
  sets: SetLog[];
  elbowFeel: Feel | null;
  forearmFeel: Feel | null;
  gripVariant: GripVariant | null;
  note: string;
}

export interface Session {
  id: string;
  // ISO date (YYYY-MM-DD) of the workout.
  date: string;
  // Which gym this was trained in. null only for pre-gym-tracking history that
  // predates the backfill; treated as "unknown" and excluded from gym-scoped
  // targets rather than guessed at.
  gymId: GymId | null;
  routineId: string | null;
  routineLabel: string;
  bodyweightKg: number | null;
  sessionNote: string;
  entries: ExerciseEntry[];
  createdAt: string;
  updatedAt: string;
  // null while the session is an in-progress draft; set when finished. Only
  // finished sessions sync to Notion and count as "beat this" targets / volume.
  finishedAt: string | null;
  // Cowork-owned coaching fields, pulled back from Notion for display. The app
  // never writes these to Notion (conflict-free split: app owns the log).
  coaching: string | null;
  suggestedAdjustments: SuggestedAdjustment[] | null;
  coachedAt: string | null;
}

export interface RoutineSlot {
  exerciseId: string;
  targetSets: number;
}

export interface Routine {
  id: string;
  dayLabel: string;
  // 1 = Monday ... 7 = Sunday. Used to auto-suggest today's routine.
  weekday: number | null;
  slots: RoutineSlot[];
}

export type AdjustmentKind = 'load' | 'swap' | 'deload' | 'hold' | 'note';

// Structured suggestion the Cowork coach writes into Notion and the app reads
// back. Judgement on top of the deterministic engine. A 'hold'/'deload'/'swap'
// legitimately overrides the engine on injury/recovery grounds (with a reason);
// the app honors it for the next session only, then reverts to engine-driven.
export interface SuggestedAdjustment {
  exerciseId: string | null;
  exerciseName: string | null;
  kind: AdjustmentKind;
  detail: string;
  // Optional concrete override the planner can apply to next session's target.
  suggestedWeightKg: number | null;
  suggestedRepRange: RepRange | null;
  // kind 'swap': the exercise to use instead. The coach usually writes the name;
  // accept-time resolves it to an id. The slot is substituted on next session start.
  swapToExerciseId?: string | null;
  swapToExerciseName?: string | null;
  // Grip bias to pre-select on the entry next session (curls etc.).
  suggestedGrip?: GripVariant | null;
  // The gym of the session this advice was written about, stamped when accepted.
  // An absolute suggestedWeightKg only means something on that gym's equipment, so
  // it is dropped when the advice is applied elsewhere (see adjustmentForGym). The
  // intent — hold, deload, swap, grip, rep range — is about his elbow, not the
  // machine, and always carries across. null for pre-gym-tracking advice.
  sourceGymId?: GymId | null;
}

// Body measurement record. Mirrors Marko's real measuring sheet: per-bodypart, with
// relax/flex and left/right where he tracks both sides. All circumferences in cm,
// bodyweight in kg. Every metric is optional (he doesn't measure every part each time).
// Field names map 1:1 to the Notion "Body Measurements" DB columns (see PROP.measurements).
export interface BodyMeasurement {
  id: string;
  // ISO date (YYYY-MM-DD). One measurement per day; Notion upserts by this date.
  date: string;
  bodyweightKg: number | null;
  // Torso
  waistCm: number | null; // over the glutes/hips, widest point above the penis
  bellyCm: number | null; // at the navel
  chestRelaxCm: number | null;
  chestFlexCm: number | null;
  shouldersCm: number | null;
  neckCm: number | null;
  // Arms (L/R, relaxed/flexed)
  armRelaxLCm: number | null;
  armRelaxRCm: number | null;
  armFlexLCm: number | null;
  armFlexRCm: number | null;
  // Thighs (L/R, relaxed/flexed)
  thighRelaxLCm: number | null;
  thighRelaxRCm: number | null;
  thighFlexLCm: number | null;
  thighFlexRCm: number | null;
  // Lower / wrists / forearms (L/R)
  calfLCm: number | null;
  calfRCm: number | null;
  wristLCm: number | null;
  wristRCm: number | null;
  forearmFlexLCm: number | null;
  forearmFlexRCm: number | null;
  // Free-text context, e.g. "after breakfast, gym free day" (Notion "Context").
  note: string;
}

export type OutboxStatus = 'pending' | 'synced' | 'error';

export interface OutboxItem {
  // Equals the session id, so sync is idempotent (upsert by session id in Notion).
  sessionId: string;
  status: OutboxStatus;
  attempts: number;
  lastError: string | null;
  updatedAt: string;
}

// Separate outbox for body measurements (keyed by measurement id). Same drain
// mechanics as the session outbox; the Worker upserts by date so re-syncs are safe.
export interface MeasurementOutboxItem {
  measurementId: string;
  status: OutboxStatus;
  attempts: number;
  lastError: string | null;
  updatedAt: string;
}

// The numeric measurement fields (everything chartable). Single source of truth for
// the form, the trend picker, and the Notion property mapper so they never drift.
export type MeasurementField = Exclude<keyof BodyMeasurement, 'id' | 'date' | 'note'>;
